import { z } from 'zod';

export type SessionType = 'planned' | 'ad_hoc' | 'template_based';

export interface WorkoutSession {
  readonly id: string;
  readonly userId: string;
  readonly programId: string | null;
  readonly programDayId: string | null;
  readonly title: string | null;
  readonly sessionType: SessionType;
  readonly startedAt: string;
  readonly completedAt: string | null;
  readonly notes: string | null;
}

export interface WorkoutSet {
  readonly id: string;
  readonly workoutSessionId: string;
  readonly exerciseId: string;
  readonly orderIndex: number;
  readonly setIndex: number;
  readonly weight: number | null;
  readonly reps: number | null;
  readonly durationSeconds: number | null;
  readonly distanceMeters: number | null;
  readonly rpe: number | null;
  readonly completed: boolean;
  /**
   * True if the user explicitly skipped this planned set during a guided
   * session. Skipped sets keep their slot/order so set numbering stays stable
   * (#2 of 5 stays #2 after skip), but they are excluded from PRs, totals,
   * and set counts.
   */
  readonly skipped: boolean;
  /** When the user actually began this set in guided mode (null in manual mode). */
  readonly startedAt: string | null;
  readonly completedAt: string | null;
}

/**
 * One exercise within a session: id + display fields + its sets.
 */
export interface SessionExerciseGroup {
  readonly exerciseId: string;
  readonly exerciseName: string;
  readonly category: string;
  readonly equipment: string | null;
  readonly metrics: readonly ('weight' | 'reps' | 'duration' | 'distance')[];
  readonly orderIndex: number;
  /**
   * Coach-prescribed rest between sets, in seconds. Resolved from the program
   * slot at session load. Null when ad-hoc or no target was set; the guided
   * RestView then falls back to a count-up with no audio cue.
   */
  readonly plannedRestSeconds: number | null;
  readonly sets: readonly WorkoutSet[];
}

export interface WorkoutSessionFull extends WorkoutSession {
  readonly groups: readonly SessionExerciseGroup[];
}

// ---------- Zod schemas ----------

export const startAdHocSchema = z.object({
  title: z.string().trim().max(80).optional().or(z.literal('')),
});
export type StartAdHocFormInput = z.infer<typeof startAdHocSchema>;

/**
 * Domain input for starting an ad-hoc session. `targetUserId` defaults to the
 * current user; when set to a managed-ghost profile id the session is created
 * under that ghost (RLS allows this when the ghost has `managed_by = me`).
 * Used by the coach-mode "start workout for athlete" flow.
 */
export interface StartAdHocInput {
  readonly title?: string;
  readonly targetUserId?: string;
}

/**
 * Backfill = log a workout that already happened on a past day. Both
 * started_at and completed_at are set to the chosen calendar date so the
 * workout appears on that day in the calendar/history; the user fills in
 * exercises and sets after creation.
 */
export interface StartBackfillInput {
  /** ISO date string (YYYY-MM-DD) for the past calendar day. */
  readonly date: string;
  readonly title?: string;
}

export interface StartFromProgramDayInput {
  readonly programId: string;
  readonly programDayId: string;
  readonly title?: string;
}

/**
 * Patch shape for one set — same form-string strategy as program slots.
 */
export const setPatchSchema = z.object({
  weight: z.string().optional(),
  reps: z.string().optional(),
  durationSeconds: z.string().optional(),
  distanceMeters: z.string().optional(),
  rpe: z.string().optional(),
});
export type SetPatchForm = z.infer<typeof setPatchSchema>;

export interface SetPatch {
  readonly weight?: number | null;
  readonly reps?: number | null;
  readonly durationSeconds?: number | null;
  readonly distanceMeters?: number | null;
  readonly rpe?: number | null;
  readonly completed?: boolean;
  readonly skipped?: boolean;
}

function parseOptionalNumber(value: string | undefined): number | null | undefined {
  if (value === undefined) return undefined;
  if (value.trim() === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function setFormToPatch(form: SetPatchForm): SetPatch {
  const weight = parseOptionalNumber(form.weight);
  const reps = parseOptionalNumber(form.reps);
  const durationSeconds = parseOptionalNumber(form.durationSeconds);
  const distanceMeters = parseOptionalNumber(form.distanceMeters);
  const rpe = parseOptionalNumber(form.rpe);
  return {
    ...(weight !== undefined && { weight }),
    ...(reps !== undefined && { reps }),
    ...(durationSeconds !== undefined && { durationSeconds }),
    ...(distanceMeters !== undefined && { distanceMeters }),
    ...(rpe !== undefined && { rpe }),
  };
}
